import React, { useEffect, useState } from 'react'
import { Button, Input, Form, Typography, message, Select } from 'antd'
import { ClientProfile, VendorType } from 'types'
import updateVendorDetails, {
	VendorDetailsBody,
} from 'api/clients/updateVendorDetails'
import handleFormError from 'utils/handleFormError'

const { Item } = Form
const { Title } = Typography
const { Option } = Select

interface PropTypes {
	clientData: ClientProfile | null
	onSuccess: Function
}

const UpdateVendorDetailsForm: React.FC<PropTypes> = ({
	clientData,
	onSuccess,
}) => {
	const [form] = Form.useForm()
	const [loader, setLoader] = useState(false)
	const [selectedType, setSelectedType] = useState<VendorType | undefined>()

	useEffect(() => {
		setSelectedType(clientData?.vendorSelected)
	}, [clientData])

	const onVendorChange = (value: VendorType) => {
		setSelectedType(value)
	}

	/**
	 * Form values type
	 */
	interface FormValues {
		appName: string
		apiKey: string
		sourceWaNumber: string
		wabaId: string
		vendorSelected: VendorType
		gupshup_user_id: string
		gupshup_user_password: string
		gupshup_two_way_user: string
		gupshup_two_way_password: string
		ts_apiKey: string
		wa_token: string
		wa_phone_id: string
		airtelCustomerId: string
		airtelUsername: string
		airtelPassword: string
		ts_channelId: string
		pinnacleApiKey: string
	}

	/**
	 * Update the Vendor details for Multi-Vendor on form submission
	 * @param values : get values on form submission to apdate vendor details
	 */
	const onFinish = async (values: FormValues) => {
		setLoader(true)
		if (clientData !== null) {
			const body: VendorDetailsBody = {
				appName: '',
				apiKey: '',
				wabaId: '',
				sourceWaNumber: values.sourceWaNumber,
				vendorSelected: values.vendorSelected,
				userId: '',
				password: '',
				two_way_userId: '',
				two_way_password: '',
				ts_apiKey: '',
				wa_token: '',
				wa_phone_id: '',
				airtelCustomerId: '',
				airtelPassword: '',
				airtelUsername: '',
				ts_channelId: '',
				pinnacleApiKey: '',
			}

			if (values.vendorSelected === 'GUPSHUP') {
				body.appName = values.appName
				body.apiKey = values.apiKey
				body.wabaId = values.wabaId
			}

			if (values.vendorSelected === 'GUPSHUP_ENTERPRISE') {
				body.userId = values.gupshup_user_id
				body.password = values.gupshup_user_password
				body.two_way_userId = values.gupshup_two_way_user
				body.two_way_password = values.gupshup_two_way_password
			}

			if (values.vendorSelected === 'THREE_SIXTY_DIALOG') {
				body.ts_apiKey = values.ts_apiKey
				body.wabaId = values.wabaId
			}

			if (values.vendorSelected === 'THREE_SIXTY_DIALOG_CLOUD') {
				body.ts_channelId = values.ts_channelId
				body.wabaId = values.wabaId
			}

			if (values.vendorSelected === 'WHATSAPP_CLOUD') {
				body.wabaId = values.wabaId
				body.wa_token = values.wa_token
				body.wa_phone_id = values.wa_phone_id
				body.sourceWaNumber = values.sourceWaNumber
			}
			if (values.vendorSelected === 'PINNACLE') {
				body.wabaId = values.wabaId
				body.sourceWaNumber = values.sourceWaNumber
				body.wa_phone_id = values.wa_phone_id
				body.wa_token = values.wa_token
				body.pinnacleApiKey = values.pinnacleApiKey
			}

			if (values.vendorSelected === 'PINNACLE_V3') {
				body.wabaId = values.wabaId
				body.sourceWaNumber = values.sourceWaNumber
				body.wa_phone_id = values.wa_phone_id
				body.wa_token = values.wa_token
				body.pinnacleApiKey = values.pinnacleApiKey
			}

			if (values.vendorSelected === 'AIRTEL') {
				body.wabaId = values.wabaId
				body.airtelCustomerId = values.airtelCustomerId
				body.airtelUsername = values.airtelUsername
				body.airtelPassword = values.airtelPassword
			}

			try {
				await updateVendorDetails(clientData?._id, body)
				message.success('Vendor details updated!')
				onSuccess(true)
				setLoader(false)
			} catch (error) {
				onSuccess(false)
				setLoader(false)
			}
		} else {
			setLoader(false)
		}
	}

	return (
		<>
			<div
				className='head-align-flex'
				style={{ paddingBottom: '1rem', marginTop: '1rem' }}
			>
				<div className='Title-Head'>
					<Title level={5}>Vendor Details</Title>
				</div>
			</div>
			<Form
				form={form}
				name='updateVendorDetailsForm'
				onFinish={onFinish}
				onFinishFailed={handleFormError}
				autoComplete='off'
				layout='vertical'
				initialValues={{
					appName: clientData?.appName,
					apiKey: clientData?.apiKey,
					sourceWaNumber: clientData?.sourceWaNumber,
					wabaId: clientData?.wabaId,
					vendorSelected: clientData?.vendorSelected,
					gupshup_user_id: clientData?.gupshup_user_id,
					gupshup_user_password: atob(clientData?.gupshup_user_password || ''),
					gupshup_two_way_user: clientData?.gupshup_two_way_user || '',
					gupshup_two_way_password: atob(clientData?.gupshup_two_way_password || ''),
					ts_apiKey: clientData?.ts_apiKey,
					wa_token: clientData?.wa_token,
					wa_phone_id: clientData?.wa_phone_id,
					airtelCustomerId: clientData?.airtelCustomerId,
					airtelUsername: clientData?.airtelUsername,
					airtelPassword: clientData?.airtelPassword,
					ts_channelId: clientData?.ts_channelId,
					pinnacleApiKey: clientData?.pinnacleApiKey,
				}}
				wrapperCol={{ span: 10 }}
			>
				<Item
					name='vendorSelected'
					label='Select Vendor'
					rules={[{ required: true, message: 'Please select vendor' }]}
				>
					<Select placeholder='Select vendor' onChange={onVendorChange}>
						<Option value='GUPSHUP'>GUPSHUP</Option>
						<Option value='GUPSHUP_ENTERPRISE'>GUPSHUP ENTERPRISE</Option>
						<Option value='THREE_SIXTY_DIALOG'>360 DIALOG</Option>
						<Option value='THREE_SIXTY_DIALOG_CLOUD'>360 DIALOG CLOUD</Option>
						<Option value='WHATSAPP_CLOUD'>WHATSAPP CLOUD</Option>
						<Option value='AIRTEL'>AIRTEL</Option>
						<Option value='PINNACLE'>PINNACLE</Option>
						<Option value='PINNACLE_V3'>PINNACLE V3</Option>
					</Select>
				</Item>
				<Item
					label='App Name'
					name='appName'
					rules={[
						{
							required: selectedType === 'GUPSHUP',
							message: 'Please enter App Name',
						},
					]}
					hidden={selectedType !== 'GUPSHUP'}
				>
					<Input />
				</Item>
				<Item
					label='API Key'
					name='apiKey'
					rules={[
						{ required: selectedType === 'GUPSHUP', message: 'Please enter API Key' },
					]}
					hidden={selectedType !== 'GUPSHUP'}
				>
					<Input />
				</Item>
				<Item
					label='wabaId'
					name='wabaId'
					rules={[
						{
							required:
								selectedType === 'GUPSHUP' ||
								selectedType === 'THREE_SIXTY_DIALOG' ||
								selectedType === 'THREE_SIXTY_DIALOG_CLOUD' ||
								selectedType === 'WHATSAPP_CLOUD' ||
								selectedType === 'AIRTEL' ||
								selectedType === 'PINNACLE' ||
								selectedType === 'PINNACLE_V3',
							message: 'Please enter wabaId',
						},
					]}
					hidden={
						!(
							selectedType === 'GUPSHUP' ||
							selectedType === 'THREE_SIXTY_DIALOG' ||
							selectedType === 'THREE_SIXTY_DIALOG_CLOUD' ||
							selectedType === 'WHATSAPP_CLOUD' ||
							selectedType === 'AIRTEL' ||
							selectedType === 'PINNACLE' ||
							selectedType === 'PINNACLE_V3'
						)
					}
				>
					<Input />
				</Item>

				<Item
					label='Source WA Number'
					name='sourceWaNumber'
					rules={[{ required: true, message: 'Please enter Source WA Number' }]}
				>
					<Input />
				</Item>
				<Item
					label='Gupshup User ID'
					name='gupshup_user_id'
					rules={[
						{
							required: selectedType === 'GUPSHUP_ENTERPRISE',
							message: 'Please enter User ID',
						},
					]}
					hidden={selectedType !== 'GUPSHUP_ENTERPRISE'}
				>
					<Input />
				</Item>
				<Item
					label='Gupshup User Password'
					name='gupshup_user_password'
					rules={[
						{
							required: selectedType === 'GUPSHUP_ENTERPRISE',
							message: 'Please enter User Password',
						},
					]}
					hidden={selectedType !== 'GUPSHUP_ENTERPRISE'}
				>
					<Input.Password />
				</Item>
				<Item
					label='Gupshup Two Way User ID'
					name='gupshup_two_way_user'
					rules={[
						{
							required: selectedType === 'GUPSHUP_ENTERPRISE',
							message: 'Please enter Two Way User ID',
						},
					]}
					hidden={selectedType !== 'GUPSHUP_ENTERPRISE'}
				>
					<Input />
				</Item>
				<Item
					label='Gupshup Two Way User Password'
					name='gupshup_two_way_password'
					rules={[
						{
							required: selectedType === 'GUPSHUP_ENTERPRISE',
							message: 'Please enter Two Way User Password',
						},
					]}
					hidden={selectedType !== 'GUPSHUP_ENTERPRISE'}
				>
					<Input.Password />
				</Item>

				<Item
					label='360 Dialog Channel ID'
					name='ts_channelId'
					rules={[
						{
							required: selectedType === 'THREE_SIXTY_DIALOG_CLOUD',
							message: 'Please enter Channel ID',
						},
					]}
					hidden={selectedType !== 'THREE_SIXTY_DIALOG_CLOUD'}
				>
					<Input />
				</Item>

				<Item
					label='360 Dialog API Key'
					name='ts_apiKey'
					rules={[
						{
							required: selectedType === 'THREE_SIXTY_DIALOG',
							message: 'Please enter API Key',
						},
					]}
					hidden={selectedType !== 'THREE_SIXTY_DIALOG'}
				>
					<Input />
				</Item>

				<Item
					label='WhatsApp Cloud Token'
					name='wa_token'
					rules={[
						{
							required:
								selectedType === 'WHATSAPP_CLOUD' ||
								selectedType === 'PINNACLE' ||
								selectedType === 'PINNACLE_V3',
							message: 'Please enter token',
						},
					]}
					hidden={
						!(
							selectedType === 'WHATSAPP_CLOUD' ||
							selectedType === 'PINNACLE' ||
							selectedType === 'PINNACLE_V3'
						)
					}
				>
					<Input />
				</Item>

				<Item
					label='WhatsApp Phone ID'
					name='wa_phone_id'
					rules={[
						{
							required:
								selectedType === 'WHATSAPP_CLOUD' ||
								selectedType === 'PINNACLE' ||
								selectedType === 'PINNACLE_V3',
							message: 'Please enter phone ID',
						},
					]}
					hidden={
						!(
							selectedType === 'WHATSAPP_CLOUD' ||
							selectedType === 'PINNACLE' ||
							selectedType === 'PINNACLE_V3'
						)
					}
				>
					<Input />
				</Item>

				<Item
					label='Pinnacle API Key'
					name='pinnacleApiKey'
					rules={[
						{
							required: selectedType === 'PINNACLE' || selectedType === 'PINNACLE_V3',
							message: 'Please enter API Key',
						},
					]}
					hidden={selectedType !== 'PINNACLE' && selectedType !== 'PINNACLE_V3'}
				>
					<Input />
				</Item>

				<Item
					label='Airtel Customer ID'
					name='airtelCustomerId'
					rules={[
						{
							required: selectedType === 'AIRTEL',
							message: 'Please enter Airtel Customer ID',
						},
					]}
					hidden={selectedType !== 'AIRTEL'}
				>
					<Input />
				</Item>

				<Item
					label='Airtel Username'
					name='airtelUsername'
					rules={[
						{
							required: selectedType === 'AIRTEL',
							message: 'Please enter Airtel Username',
						},
					]}
					hidden={selectedType !== 'AIRTEL'}
				>
					<Input />
				</Item>

				<Item
					label='Airtel Password'
					name='airtelPassword'
					rules={[
						{
							required: selectedType === 'AIRTEL',
							message: 'Please enter Airtel Password',
						},
					]}
					hidden={selectedType !== 'AIRTEL'}
				>
					<Input.Password />
				</Item>

				<Item>
					<Button type='primary' htmlType='submit' loading={loader}>
						Update
					</Button>
				</Item>
			</Form>
		</>
	)
}

export default UpdateVendorDetailsForm
